import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField as MuiTextField,
    Typography
} from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import {ChangeEvent, CSSProperties, Dispatch, DragEvent, MouseEvent, SetStateAction, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {FiCornerUpRight, FiFile, FiFolder, FiPlus, FiRotateCw, FiTrash} from "react-icons/fi";
import {UTIF} from "../Note/UTIF_MOD";
import {
    Button as RaButton,
    BulkDeleteButton,
    Datagrid,
    DateField,
    FunctionField,
    ListProps,
    AutocompleteInput,
    ReferenceInput,
    SelectInput,
    TextField,
    TextInput,
    NullableBooleanInput,
    useGetList,
    useListContext,
    useNotify,
    useRecordContext,
    useRefresh,
    useUnselectAll
} from "react-admin";
import {CustomList} from "../../components/CustomList";
import {IdentifierField} from "../../components/IdentifierField";
import {clientNoJson, endpoint} from "../../dataProvider";
import {useOptionsResource} from "../../shared/hooks/useOptionsResource";
import {getDefaultListProps, initField} from "../../shared/utils";
import {NoteImportListHeader} from "./NoteImportListHeader";
import {DateInput} from "ra-ui-materialui";
import {BiMinus, BiPlus} from "react-icons/bi";

type NoteConfiguration = {
    id: number;
    configuration: string;
    client_name?: string;
    client_business_name?: string;
    client?: { business_name?: string } | number;
};

type DeliverySearchOption = { id: number | string; label: string };

const errorMessage = (error: any, fallback: string) =>
    error?.body?.detail || error?.response?.data?.detail || error?.message || fallback;

const noteStatusChoices = [
    {value: "all_done", display_name: "Assegnate tutte"},
    {value: "all_not_done", display_name: "Nessuna assegnata"},
    {value: "exist_not_done", display_name: "Contiene non assegnate"},
    {value: "exist_done", display_name: "Contiene assegnate"},
];

const configurationClientName = (configuration: NoteConfiguration) =>
    configuration.client_business_name || configuration.client_name ||
    (configuration.client && typeof configuration.client === "object" ? configuration.client.business_name : "") || "";

const ConfigurationSelector = ({value, onChange, active}: {
    value: string;
    onChange: (value: string) => void;
    active: boolean;
}) => {
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState<NoteConfiguration[]>([]);
    const [selected, setSelected] = useState<NoteConfiguration | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!active) return;
        const timer = window.setTimeout(() => {
            setLoading(true);
            clientNoJson(`${endpoint}/note-configurations/autocomplete/?search=${encodeURIComponent(query)}`, {method: "GET"})
                .then(({data}) => setOptions(data.results || data || []))
                .catch(() => setOptions([]))
                .finally(() => setLoading(false));
        }, 250);
        return () => window.clearTimeout(timer);
    }, [active, query]);

    return <Autocomplete
        options={options}
        value={selected && String(selected.id) === value ? selected : null}
        loading={loading}
        filterOptions={(items) => items}
        getOptionLabel={(item) => [item.configuration, configurationClientName(item)].filter(Boolean).join(" — ")}
        onChange={(_, item) => {
            setSelected(item);
            onChange(item ? String(item.id) : "");
        }}
        onInputChange={(_, text, reason) => {
            if (reason !== "reset") setQuery(text);
        }}
        renderOption={(item) => <div>
            <div>{item.configuration}</div>
            <Typography variant="caption" color="textSecondary">{configurationClientName(item) || "Cliente non indicato"}</Typography>
        </div>}
        renderInput={(params) => <MuiTextField {...params} label="Cerca configurazione o cliente" variant="outlined" fullWidth required
                                                 InputProps={{...params.InputProps, endAdornment: <>{loading ? <CircularProgress color="inherit" size={20}/> : null}{params.InputProps.endAdornment}</>}}/>}
    />;
};

const DeliverySelector = ({noteImportId, fileId, searchRequest, onAssigned}: {
    noteImportId: number;
    fileId: number;
    searchRequest?: { value: string; nonce: number };
    onAssigned?: (fileId: number, deliveryId: number | string) => void;
}) => {
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState<DeliverySearchOption[]>([]);
    const [selected, setSelected] = useState<DeliverySearchOption | null>(null);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const latestQuery = useRef("");
    const notify = useNotify();
    const refresh = useRefresh();
    useEffect(() => {
        if (!searchRequest) return;
        setSelected(null);
        setQuery(searchRequest.value);
    }, [searchRequest]);
    useEffect(() => {
        if (!searchRequest) return;
        const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(timer);
    }, [searchRequest]);
    const assignDelivery = async (delivery: DeliverySearchOption, automatic = false) => {
        if (saving) return;
        setSaving(true);
        try {
            await clientNoJson(`${endpoint}/note-imports/${noteImportId}/files/${fileId}/assign-delivery/`, {
                method: "POST", data: {delivery_id: delivery.id}
            });
            notify(automatic ? "Missione trovata e bolla associata" : "Bolla associata alla delivery", "info");
            if (onAssigned) onAssigned(fileId, delivery.id);
            else refresh();
        } catch {
            notify("Impossibile associare la bolla", "warning");
        } finally {
            setSaving(false);
        }
    };
    const searchDeliveries = async (value: string, assignSingleResult = false) => {
        if (value.trim().length < 1) {
            setOptions([]);
            return;
        }
        const borderauxMatch = value.trim().match(/^B\.(\d+)$/i);
        const deliveryGroupMatch = value.trim().match(/^G\.(\d+)$/i);
        if (borderauxMatch) {
            const borderaux = {id: `B.${borderauxMatch[1]}`, label: `Giro B.${borderauxMatch[1]}`};
            setOptions([borderaux]);
            if (assignSingleResult) {
                setSelected(borderaux);
                await assignDelivery(borderaux, true);
            }
            return;
        }
        if (deliveryGroupMatch) {
            const delivery_group = {id: `G.${deliveryGroupMatch[1]}`, label: `Raggruppamento G.${deliveryGroupMatch[1]}`};
            setOptions([delivery_group]);
            if (assignSingleResult) {
                setSelected(delivery_group);
                await assignDelivery(delivery_group, true);
            }
            return;
        }
        try {
            const {data} = await clientNoJson(`${endpoint}/autocomplete/delivery/?search=${encodeURIComponent(value)}`, {method: "GET"});
            if (latestQuery.current !== value) return;
            const results = data.results || data || [];
            setOptions(results);
            if (assignSingleResult && results.length === 1) {
                setSelected(results[0]);
                await assignDelivery(results[0], true);
            }
        } catch {
            if (latestQuery.current === value) setOptions([]);
        }
    };
    useEffect(() => {
        latestQuery.current = query;
        if (query.trim().length < 1) {
            setOptions([]);
            return;
        }
        const timer = window.setTimeout(() => searchDeliveries(query), 250);
        return () => window.clearTimeout(timer);
    }, [query]);
    const save = async () => {
        if (!selected) return;
        await assignDelivery(selected);
    };
    return <div style={{display: "flex", alignItems: "center", gap: 8, minWidth: 330}}>
        <Autocomplete options={options} filterOptions={(items) => items} getOptionLabel={(option) => option.label} value={selected}
                      inputValue={query}
                      onChange={(_, value) => setSelected(value)} onInputChange={(_, value) => setQuery(value)}
                      style={{width: 260}}
                      renderInput={(params) => <MuiTextField {...params} size="small" label="Cerca delivery"
                                                             placeholder="ID o numero documento" inputRef={inputRef}
                                                             onKeyDown={(event) => {
                                                                 if (event.key !== "Enter") return;
                                                                 event.preventDefault();
                                                                 const value = (event.target as HTMLInputElement).value;
                                                                 latestQuery.current = value;
                                                                 setQuery(value);
                                                                 searchDeliveries(value, true);
                                                             }}/>}/>
        <Button size="small" color="primary" variant="contained" onClick={save} disabled={!selected || saving}>Salva</Button>
    </div>;
};

const ImportFilePreview = ({noteImportId, file, onClose, zoom, setZoom, rotation, setRotation}: {
    noteImportId: number; file: any; onClose: () => void;
    zoom: number; setZoom: Dispatch<SetStateAction<number>>;
    rotation: number; setRotation: Dispatch<SetStateAction<number>>;
}) => {
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewType, setPreviewType] = useState<"image" | "pdf">("image");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const imageSurfaceRef = useRef<HTMLDivElement>(null);
    const [surfaceSize, setSurfaceSize] = useState({width: 0, height: 0});
    const sideways = rotation % 180 !== 0;
    const viewportRef = useRef<HTMLDivElement>(null);
    const wheelAnchor = useRef<{x: number; y: number; relativeX: number; relativeY: number} | null>(null);
    const dragOrigin = useRef<{x: number; y: number; left: number; top: number} | null>(null);
    const [dragging, setDragging] = useState(false);

    const fitPreview = () => {
        wheelAnchor.current = null;
        setZoom(100);
        viewportRef.current?.scrollTo(0, 0);
    };

    useLayoutEffect(() => {
        const anchor = wheelAnchor.current;
        const viewport = viewportRef.current;
        const surface = imageSurfaceRef.current;
        if (!anchor || !viewport || !surface) return;
        wheelAnchor.current = null;
        const bounds = surface.getBoundingClientRect();
        viewport.scrollLeft = anchor.relativeX * bounds.width - anchor.x;
        viewport.scrollTop = anchor.relativeY * bounds.height - anchor.y;
    }, [zoom]);

    useEffect(() => {
        const surface = imageSurfaceRef.current;
        if (!surface) return;
        const observer = new ResizeObserver(([entry]) => {
            setSurfaceSize({width: entry.contentRect.width, height: entry.contentRect.height});
        });
        observer.observe(surface);
        return () => observer.disconnect();
    }, [loading, previewUrl, previewType]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || loading || !previewUrl || previewType !== "image") return;

        const handleWheel = (event: WheelEvent) => {
            if (!event.deltaY) return;
            event.preventDefault();
            const nextZoom = Math.max(100, Math.min(400, zoom + (event.deltaY < 0 ? 25 : -25)));
            const surface = imageSurfaceRef.current;
            if (nextZoom === zoom || !surface) return;
            const bounds = surface.getBoundingClientRect();
            const viewportBounds = viewport.getBoundingClientRect();
            if (!bounds.width || !bounds.height) return;
            wheelAnchor.current = {
                x: event.clientX - viewportBounds.left - viewport.clientLeft,
                y: event.clientY - viewportBounds.top - viewport.clientTop,
                relativeX: (event.clientX - bounds.left) / bounds.width,
                relativeY: (event.clientY - bounds.top) / bounds.height,
            };
            dragOrigin.current = null;
            setDragging(false);
            setZoom(nextZoom);
        };

        // A non-passive listener prevents page scrolling while zooming the preview.
        viewport.addEventListener("wheel", handleWheel, {passive: false});
        return () => viewport.removeEventListener("wheel", handleWheel);
    }, [loading, previewUrl, previewType, setZoom, zoom]);

    useEffect(() => {
        wheelAnchor.current = null;
        setDragging(false);
        dragOrigin.current = null;
        setPreviewUrl("");
        if (!file) {
            setPreviewUrl("");
            setError("");
            return;
        }
        let active = true;
        let objectUrl = "";
        setLoading(true);
        setError("");
        clientNoJson(`${endpoint}/note-imports/${noteImportId}/files/${file.id}/preview/`, {
            method: "GET",
            responseType: "arraybuffer",
        }).then(({data, headers}) => {
            if (!active) return;
            const name = file.file_info?.name || file.file || "";
            if (/\.tiff?$/i.test(name)) {
                const ifds = UTIF.decode(data);
                UTIF.decodeImage(data, ifds[0], ifds);
                const rgba = UTIF.toRGBA8(ifds[0]);
                const canvas = document.createElement("canvas");
                canvas.width = ifds[0].width;
                canvas.height = ifds[0].height;
                canvas.getContext("2d")?.putImageData(
                    new ImageData(new Uint8ClampedArray(rgba.buffer), ifds[0].width, ifds[0].height), 0, 0,
                );
                setPreviewType("image");
                setPreviewUrl(canvas.toDataURL());
                return;
            }
            const contentType = headers["content-type"] || (/\.pdf$/i.test(name) ? "application/pdf" : "image/*");
            objectUrl = window.URL.createObjectURL(new Blob([data], {type: contentType}));
            setPreviewType(/\.pdf$/i.test(name) ? "pdf" : "image");
            setPreviewUrl(objectUrl);
        }).catch(() => {
            if (active) setError("Impossibile visualizzare la bolla selezionata");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => {
            active = false;
            if (objectUrl) window.URL.revokeObjectURL(objectUrl);
        };
    }, [file?.id, noteImportId]);

    return <div style={{border: "1px solid #ddd", borderRadius: 4, minHeight: 420, padding: 12, backgroundColor: "#fafafa"}}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
            <Typography variant="subtitle1" gutterBottom>Anteprima bolla</Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                {previewType === "image" && <div>
                    <Button size="small" title="Riduci zoom" disabled={zoom <= 100} onClick={() => setZoom((currentZoom) => Math.max(100, currentZoom - 25))}><BiMinus/></Button>
                    <Button size="small" title="Mostra tutta la bolla" onClick={fitPreview}>{zoom}%</Button>
                    <Button size="small" title="Ingrandisci" disabled={zoom >= 400} onClick={() => setZoom((currentZoom) => Math.min(400, currentZoom + 25))}><BiPlus/></Button>
                    <Button size="small" title="Ruota la bolla di 90°" aria-label="Ruota la bolla di 90 gradi" disabled={loading || !previewUrl} onClick={() => {
                        setRotation((currentRotation) => (currentRotation + 90) % 360);
                        dragOrigin.current = null;
                        setDragging(false);
                        viewportRef.current?.scrollTo(0, 0);
                    }}><FiRotateCw/></Button>
                </div>}
                <Button size="small" title="Chiudi anteprima" onClick={onClose}><CloseIcon/></Button>
            </div>
        </div>
        {loading && <div style={{textAlign: "center", paddingTop: 160}}><CircularProgress/></div>}
        {error && <Typography color="error">{error}</Typography>}
        {!loading && previewUrl && <>
            <div ref={viewportRef} tabIndex={0} aria-label="Anteprima navigabile della bolla"
                 title={previewType === "image" ? "Usa la rotella per lo zoom e trascina per spostare la bolla" : undefined}
                 style={{height: "70vh", maxHeight: 720, overflow: "auto", border: "1px solid #eee", background: "#e8e8e8", cursor: previewType === "image" && zoom > 100 ? (dragging ? "grabbing" : "grab") : "auto", touchAction: previewType === "image" && zoom > 100 ? "none" : "auto"}}
                 onPointerDown={(event) => {
                     if (previewType !== "image" || zoom <= 100 || event.button !== 0) return;
                     const viewport = event.currentTarget;
                     dragOrigin.current = {x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop};
                     viewport.setPointerCapture(event.pointerId);
                     setDragging(true);
                 }}
                 onPointerMove={(event) => {
                     const origin = dragOrigin.current;
                     if (!origin) return;
                     event.currentTarget.scrollLeft = origin.left - (event.clientX - origin.x);
                     event.currentTarget.scrollTop = origin.top - (event.clientY - origin.y);
                 }}
                 onPointerUp={(event) => {
                     if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
                     dragOrigin.current = null;
                     setDragging(false);
                 }}
                 onLostPointerCapture={() => { dragOrigin.current = null; setDragging(false); }}
                 onPointerCancel={() => { dragOrigin.current = null; setDragging(false); }}>
                {previewType === "pdf"
                    ? <iframe title="Anteprima bolla" src={`${previewUrl}#view=Fit&toolbar=1`} style={{width: "100%", height: "100%", border: 0}}/>
                    : <div ref={imageSurfaceRef} style={{width: `${zoom}%`, height: `${zoom}%`, position: "relative", overflow: "hidden"}}>
                        <img src={previewUrl} draggable={false} alt={file?.file_info?.name || "Bolla"} style={{display: "block", position: "absolute", left: "50%", top: "50%", width: sideways ? surfaceSize.height : "100%", height: sideways ? surfaceSize.width : "100%", transform: `translate(-50%, -50%) rotate(${rotation}deg)`, maxWidth: "none", objectFit: "contain", userSelect: "none"}}/>
                    </div>}
            </div>
        </>}
    </div>;
};

const ImportFiles = () => {
    const record = useRecordContext<any>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [previewFile, setPreviewFile] = useState<any | null>(null);
    const [previewSettings, setPreviewSettings] = useState<{[importId: string]: {zoom: number; rotation: number}}>(() => {
        try {
            const saved = JSON.parse(window.sessionStorage.getItem("note-import-preview-settings") || "{}");
            return saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
        } catch {
            return {};
        }
    });
    const storedSettings = previewSettings[String(record?.id)];
    const zoom = storedSettings && Number.isFinite(storedSettings.zoom) ? Math.max(100, Math.min(400, storedSettings.zoom)) : 100;
    const rotation = storedSettings && [0, 90, 180, 270].includes(storedSettings.rotation) ? storedSettings.rotation : 0;
    const updatePreviewSetting = useCallback((field: "zoom" | "rotation", value: SetStateAction<number>) => {
        setPreviewSettings((current) => {
            const settings = current[String(record?.id)] || {zoom: 100, rotation: 0};
            return {...current, [String(record?.id)]: {...settings, [field]: typeof value === "function" ? value(settings[field]) : value}};
        });
    }, [record?.id]);
    const setZoom = useCallback((value: SetStateAction<number>) => updatePreviewSetting("zoom", value), [updatePreviewSetting]);
    const setRotation = useCallback((value: SetStateAction<number>) => updatePreviewSetting("rotation", value), [updatePreviewSetting]);
    useEffect(() => {
        if (!storedSettings || record?.id == null) return;
        try {
            const saved = JSON.parse(window.sessionStorage.getItem("note-import-preview-settings") || "{}");
            window.sessionStorage.setItem("note-import-preview-settings", JSON.stringify({...saved, [String(record.id)]: storedSettings}));
        } catch {
            // Keep the in-memory settings when browser storage is unavailable.
        }
    }, [storedSettings, record?.id]);
    const previewAdvanceTimer = useRef<number | null>(null);
    const [locallyAssignedDeliveries, setLocallyAssignedDeliveries] = useState<{ [fileId: number]: string }>({});
    const [deliverySearch, setDeliverySearch] = useState<{ fileId: number; value: string; nonce: number } | null>(null);
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
    const [selectionAnchor, setSelectionAnchor] = useState<number | null>(null);
    const [createImportOpen, setCreateImportOpen] = useState(false);
    const [assignImportOpen, setAssignImportOpen] = useState(false);
    const [deleteBulkFile, setDeleteBulkFile] = useState(false);
    const [configurationId, setConfigurationId] = useState("");
    const [targetImportId, setTargetImportId] = useState("");
    const [moving, setMoving] = useState(false);
    const {data: configurationsData = {}, ids: configurationIds = []} = useGetList(
        "note-configurations", {page: 1, perPage: 250}, {field: "configuration", order: "ASC"}, {},
    );
    const {data: importsData = {}, ids: importIds = []} = useGetList(
        "note-imports", {page: 1, perPage: 250}, {field: "id", order: "DESC"}, {status: "start", hide: false},
    );
    const configurations = configurationIds.map((id) => (configurationsData as any)[id]);
    const readyImports = importIds.map((id) => (importsData as any)[id]).filter((item: any) => item.id !== record?.id);
    const pageDetailsFromFile = (file: any) => {
        const name = String(file.file_info?.name || file.file || "");
        const match = name.match(/^(.*)_(?:pari|dispari|page)_(\d+)(?:_[a-z0-9]+)?(?:\.[^.]+)?$/i);
        return match ? {documentName: match[1], pageIndex: Number(match[2])} : null;
    };
    const files = [...(record?.files || [])].sort((first: any, second: any) => {
        const firstPage = pageDetailsFromFile(first);
        const secondPage = pageDetailsFromFile(second);
        if (firstPage && secondPage && firstPage.documentName === secondPage.documentName && firstPage.pageIndex !== secondPage.pageIndex) {
            return firstPage.pageIndex - secondPage.pageIndex;
        }
        const firstAssociated = Boolean(first.delivery_identifier || locallyAssignedDeliveries[first.id]);
        const secondAssociated = Boolean(second.delivery_identifier || locallyAssignedDeliveries[second.id]);
        if (firstAssociated !== secondAssociated) return firstAssociated ? 1 : -1;
        return String(first.file_info?.name || first.file || "").localeCompare(String(second.file_info?.name || second.file || ""));
    });
    const skippedFileCount = files.filter((file: any) => file.skipped).length;
    const selectableFileIds = files.filter((file: any) => !file.delivery_identifier && !locallyAssignedDeliveries[file.id] && !file.skipped && file.file).map((file: any) => file.id);
    const allFilesSelected = selectableFileIds.length > 0 && selectableFileIds.every((id) => selectedFileIds.includes(id));
    const someFilesSelected = selectableFileIds.some((id) => selectedFileIds.includes(id));
    const toggleFile = (fileId: number, event?: MouseEvent<HTMLElement>) => {
        if (event?.ctrlKey && event.shiftKey && event.button === 0 && selectionAnchor !== null) {
            const first = selectableFileIds.indexOf(selectionAnchor);
            const last = selectableFileIds.indexOf(fileId);
            if (first !== -1 && last !== -1) {
                const range = selectableFileIds.slice(Math.min(first, last), Math.max(first, last) + 1);
                setSelectedFileIds((ids) => Array.from(new Set([...ids, ...range])));
                return;
            }
        }
        setSelectionAnchor(fileId);
        setSelectedFileIds((ids) => ids.includes(fileId) ? ids.filter((id) => id !== fileId) : [...ids, fileId]);
    };
    const toggleAllFiles = (checked: boolean) => {
        setSelectedFileIds((ids) => checked
            ? Array.from(new Set([...ids, ...selectableFileIds]))
            : ids.filter((id) => !selectableFileIds.includes(id)));
    };
    const openPreview = (file: any) => {
        setPreviewFile(file);
        setDeliverySearch({fileId: file.id, value: "", nonce: Date.now()});
    };
    const advancePreview = (assignedFileId: number) => {
        const assignedIndex = files.findIndex((file: any) => file.id === assignedFileId);
        const filesAfterAssignment = [
            ...files.slice(assignedIndex + 1),
            ...files.slice(0, Math.max(assignedIndex, 0)),
        ];
        const nextFile = filesAfterAssignment.find((file: any) => file.id !== assignedFileId && !file.delivery_identifier && !locallyAssignedDeliveries[file.id] && !file.skipped);
        setPreviewFile(null);
        setDeliverySearch(null);
        if (previewAdvanceTimer.current) window.clearTimeout(previewAdvanceTimer.current);
        if (nextFile) previewAdvanceTimer.current = window.setTimeout(() => openPreview(nextFile), 0);
    };
    const handleAssigned = (fileId: number, deliveryId: number | string) => {
        setLocallyAssignedDeliveries((assigned) => ({...assigned, [fileId]: String(deliveryId)}));
        advancePreview(fileId);
    };
    if (!record) return null;
    const moveToImport = async (targetId: number) => {
        await clientNoJson(`${endpoint}/note-imports/${targetId}/files/assign/`, {
            method: "POST", data: {ids: selectedFileIds},
        });
        setSelectedFileIds([]);
        setPreviewFile(null);
        refresh();
    };
    const createImport = async () => {
        if (!configurationId) {
            notify("Seleziona una configurazione", "warning");
            return;
        }
        setMoving(true);
        try {
            const {data: created} = await clientNoJson(`${endpoint}/note-imports/`, {
                method: "POST", data: {configuration: Number(configurationId)},
            });
            await moveToImport(created.id);
            setCreateImportOpen(false);
            setConfigurationId("");
            notify("Nuovo import creato e file assegnati", "info");
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile creare il nuovo import"), "warning");
        } finally {
            setMoving(false);
        }
    };
    const assignToImport = async () => {
        if (!targetImportId) {
            notify("Seleziona un import pronto", "warning");
            return;
        }
        setMoving(true);
        try {
            await moveToImport(Number(targetImportId));
            setAssignImportOpen(false);
            setTargetImportId("");
            notify("File assegnati all'import selezionato", "info");
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile assegnare i file all'import"), "warning");
        } finally {
            setMoving(false);
        }
    };
    const moveToUnassignedNotes = async () => {
        setMoving(true);
        try {
            const {data} = await clientNoJson(`${endpoint}/note-imports/${record.id}/files/move-to-notes/`, {
                method: "POST", data: {ids: selectedFileIds},
            });
            setSelectedFileIds([]);
            setPreviewFile(null);
            notify(data.moved ? `${data.moved} bolle spostate in assegna bolle` : "Nessuna bolla da spostare", "info");
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile spostare le bolle selezionate"), "warning");
        } finally {
            setMoving(false);
        }
    };
    const remove = async (fileId: number) => {
        if (!window.confirm("Eliminare questa bolla dall'import?")) return;
        try {
            await clientNoJson(`${endpoint}/note-imports/${record.id}/files/${fileId}/`, {method: "DELETE"});
            setSelectedFileIds((ids) => ids.filter((id) => id !== fileId));
            if (previewFile?.id === fileId) setPreviewFile(null);
            notify("Bolla eliminata dall'import", "info");
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile eliminare la bolla"), "warning");
        }
    };
    const bulk_delete_note = async () => {
        try {
            await clientNoJson(`${endpoint}/note-imports/${record.id}/files/${selectedFileIds.join(",")}/`, {method: "DELETE"});
            setSelectedFileIds([]);
            notify("Bolla eliminata dall'import", "info");
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile eliminare le bolle"), "warning");
        }
    };
    const deleteSkippedFiles = async () => {
        if (!window.confirm(`Eliminare definitivamente ${skippedFileCount} pagine saltate?`)) return;
        setMoving(true);
        try {
            const {data} = await clientNoJson(`${endpoint}/note-imports/${record.id}/delete-skipped-files/`, {method: "POST"});
            notify(data.deleted ? `${data.deleted} pagine saltate eliminate` : "Nessuna pagina saltata da eliminare", "info");
            setSelectedFileIds([]);
            setPreviewFile(null);
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile eliminare le pagine saltate"), "warning");
        } finally {
            setMoving(false);
        }
    };
    return <div style={{display: "flex", alignItems: "flex-start", gap: 16, margin: 12}}>
        {previewFile && <div style={{flex: "1 1 50%", minWidth: 0}}><ImportFilePreview noteImportId={record.id} file={previewFile} onClose={() => setPreviewFile(null)} zoom={zoom} setZoom={setZoom} rotation={rotation} setRotation={setRotation}/></div>}
        <div style={{flex: previewFile ? "1 1 50%" : "1 1 100%", minWidth: 0, overflowX: "auto"}}>
        <div style={{display: "flex", gap: 8, marginBottom: 8, width: "max-content", minWidth: "100%", whiteSpace: "nowrap"}}>
            <Button size="small" variant="outlined" color="primary" startIcon={<FiPlus/>} disabled={!selectedFileIds.length || moving} onClick={() => setCreateImportOpen(true)}>Crea nuovo import</Button>
            <Button size="small" variant="outlined" color="primary" startIcon={<FiCornerUpRight/>} disabled={!selectedFileIds.length || moving} onClick={() => setAssignImportOpen(true)}>Assegna a import</Button>
            <Button size="small" variant="outlined" color="primary" startIcon={<FiCornerUpRight/>} disabled={!selectedFileIds.length || moving} onClick={moveToUnassignedNotes}>Sposta in assegna bolle</Button>
            <Button size="small" variant="outlined" color="secondary" startIcon={<FiTrash/>} disabled={!skippedFileCount || moving || record.status === "in_queue" || record.status === "in_progress"} onClick={deleteSkippedFiles}>Pulisci pagine saltate</Button>
            <Button size="small" variant="outlined" color="primary" startIcon={<FiTrash/>} disabled={!selectedFileIds.length || moving} onClick={() => setDeleteBulkFile(true)}>Elimina selezionati</Button>
        </div>
        <Table size="small" style={{width: "100%"}}>
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox"><Checkbox checked={allFilesSelected} indeterminate={someFilesSelected && !allFilesSelected}
                                                       disabled={!selectableFileIds.length} inputProps={{"aria-label": "Seleziona tutte le bolle non associate e non saltate"}}
                                                       onChange={(event) => toggleAllFiles(event.target.checked)}/></TableCell>
                <TableCell>File</TableCell>
                <TableCell>Testo estratto</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell/>
            </TableRow></TableHead>
        <TableBody>
            {!files.length ? <TableRow><TableCell colSpan={5}>Nessun file
                caricato.</TableCell></TableRow> : files.map((file: any) => <TableRow key={file.id}>
                <TableCell padding="checkbox">{!file.delivery_identifier && !locallyAssignedDeliveries[file.id] && !file.skipped && file.file && <Checkbox checked={selectedFileIds.includes(file.id)}
                                                                                              onClick={(event) => toggleFile(file.id, event)} onChange={() => undefined}/>}</TableCell>
                <TableCell
                    style={{backgroundColor: file.skipped ? "#fff3e0" : file.delivery_identifier || locallyAssignedDeliveries[file.id] ? "#e8f5e9" : "#ffebee"}}>{file.delivery_identifier || locallyAssignedDeliveries[file.id] ? (file.file_info?.name || file.file || "File elaborato") : <Button color="primary" size="small" startIcon={<FiFile/>} onClick={() => openPreview(file)}>{file.file_info?.name || file.file || "File elaborato"}</Button>}</TableCell><TableCell
                style={{backgroundColor: file.skipped ? "#fff3e0" : file.delivery_identifier || locallyAssignedDeliveries[file.id] ? "#e8f5e9" : "#ffebee"}}>{file.skipped ? file.skip_reason : file.delivery_identifier || locallyAssignedDeliveries[file.id] || !file.text_extract ? (file.text_extract || "-") : file.text_extract.split(",").map((documentNumber: string) => {
                    const value = documentNumber.trim();
                    return value ? <Button key={value} size="small" color="primary" onClick={() => setDeliverySearch({fileId: file.id, value, nonce: Date.now()})}>{value}</Button> : null;
                })}</TableCell><TableCell
                style={{backgroundColor: file.skipped ? "#fff3e0" : file.delivery_identifier || locallyAssignedDeliveries[file.id] ? "#e8f5e9" : "#ffebee"}}>{file.skipped ? <Chip size="small" label="Saltata volontariamente" style={{backgroundColor: "#ed6c02", color: "white"}}/> : file.delivery_identifier || locallyAssignedDeliveries[file.id] ? `Associata: ${file.delivery_identifier || locallyAssignedDeliveries[file.id]}` :
                <DeliverySelector noteImportId={record.id} fileId={file.id}
                                  searchRequest={deliverySearch && deliverySearch.fileId === file.id ? deliverySearch : undefined}
                                  onAssigned={previewFile ? handleAssigned : undefined}/>}</TableCell>
                <TableCell align="right"><Button size="small" color="secondary" title="Elimina bolla dall'import"
                                                   onClick={() => remove(file.id)}><DeleteIcon/></Button></TableCell>
            </TableRow>)}
        </TableBody>
        </Table>
        <Dialog open={createImportOpen} onClose={() => !moving && setCreateImportOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Crea nuovo import</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>{selectedFileIds.length} file selezionati</Typography>
                <Select fullWidth value={configurationId} displayEmpty onChange={(event) => setConfigurationId(event.target.value as string)}>
                    <MenuItem value="" disabled>Seleziona configurazione</MenuItem>
                    {configurations.map((configuration: any) => <MenuItem key={configuration.id} value={String(configuration.id)}>{configuration.configuration}</MenuItem>)}
                </Select>
            </DialogContent>
            <DialogActions><Button onClick={() => setCreateImportOpen(false)} disabled={moving}>Annulla</Button><Button color="primary" variant="contained" onClick={createImport} disabled={moving}>Crea</Button></DialogActions>
        </Dialog>
        <Dialog open={assignImportOpen} onClose={() => !moving && setAssignImportOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Assegna a import</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>{selectedFileIds.length} file selezionati</Typography>
                <Select fullWidth value={targetImportId} displayEmpty onChange={(event) => setTargetImportId(event.target.value as string)}>
                    <MenuItem value="" disabled>Seleziona import pronto</MenuItem>
                    {readyImports.map((item: any) => <MenuItem key={item.id} value={String(item.id)}>#{item.id} — {item.configuration_name}</MenuItem>)}
                </Select>
            </DialogContent>
            <DialogActions><Button onClick={() => setAssignImportOpen(false)} disabled={moving}>Annulla</Button><Button color="primary" variant="contained" onClick={assignToImport} disabled={moving}>Assegna</Button></DialogActions>
        </Dialog>
        <Dialog open={deleteBulkFile} onClose={() => !moving && setDeleteBulkFile(false)} fullWidth maxWidth="sm">
            <DialogTitle>Elimina file selezionati</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>Sei sicuro di voler eliminare {selectedFileIds.length} file selezionati?</Typography>
            </DialogContent>
            <DialogActions><Button onClick={() => setDeleteBulkFile(false)} disabled={moving}>Annulla</Button><Button color="primary" variant="contained" onClick={bulk_delete_note} disabled={moving}>Elimina</Button></DialogActions>
        </Dialog>
        </div>
    </div>;
};

const ImportActions = () => {
    const record = useRecordContext<any>();
    const notify = useNotify();
    const refresh = useRefresh();
    if (!record) return null;
    const request = async (path: string, message: string) => {
        try {
            await clientNoJson(`${endpoint}/note-imports/${record.id}/${path}`, {method: "POST"});
            notify(message, "info");
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Operazione non riuscita"), "warning");
        }
    };
    const toggleVisibility = async () => {
        try {
            await clientNoJson(`${endpoint}/note-imports/${record.id}/toggle-hide/`, {method: "POST"});
            notify(record.hide ? "Import reso visibile" : "Import nascosto", "info");
            refresh();
        } catch {
            notify("La visibilità può essere modificata solo per import completati", "warning");
        }
    };
    const remove = async () => {
        if (!window.confirm("Eliminare definitivamente questo import e tutte le sue bolle?")) return;
        try {
            await clientNoJson(`${endpoint}/note-imports/${record.id}/`, {method: "DELETE"});
            notify("Import eliminato", "info");
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile eliminare l'import"), "warning");
        }
    };
    return <>
        {(record.status === "start" || record.status === "error") &&
            <Button size="small" startIcon={<PlayArrowIcon/>} color="primary"
                    onClick={() => request("execute/", "Import avviato")}>Avvia</Button>}
        {record.status === "in_progress" &&
            <Button size="small" startIcon={<FiRotateCw/>} color="primary"
                    onClick={() => request("resume/", "Ripresa import richiesta")}>Riprendi import</Button>}
        {record.status === "completed" && <Button size="small" color="primary"
                                                  onClick={() => request("assign-deliveries/", "Assegnazione completata")}>Assegna
            delivery</Button>}
        {record.status === "completed" && <Button size="small" onClick={toggleVisibility}
                                                  startIcon={record.hide ? <VisibilityIcon/> : <VisibilityOffIcon/>}>
            {record.hide ? "Mostra" : "Nascondi"}
        </Button>}
        <Button size="small" color="secondary" startIcon={<DeleteIcon/>}
                disabled={record.status === "in_queue" || record.status === "in_progress"}
                onClick={remove}>Elimina</Button>
    </>;
};

const FtpPicker = ({source, configurationId, paths, onChange, active, daysBack}: {source: "static" | "client"; configurationId: string; paths: string[]; onChange: (paths: string[]) => void; active: boolean; daysBack: number}) => {
    const [path, setPath] = useState("");
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [connectionError, setConnectionError] = useState("");
    const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);
    const notify = useNotify();
    const load = useCallback(async (nextPath = "") => {
        if (source === "client" && !configurationId) return;
        setLoading(true);
        setConnectionError("");
        try {
            const query = new URLSearchParams({source, path: nextPath, days_back: String(daysBack)});
            if (source === "client") query.set("configuration_id", configurationId);
            const {data} = await clientNoJson(`${endpoint}/note-imports/ftp-files/?${query.toString()}`, {method: "GET"});
            setPath(data.path || "");
            setItems(data.items || []);
        } catch (error: any) {
            const message = errorMessage(error, "Impossibile collegarsi alla cartella FTP");
            setItems([]);
            setConnectionError(message);
            notify(message, "warning");
        }
        finally { setLoading(false); }
    }, [configurationId, daysBack, notify, source]);
    useEffect(() => {
        if (!active) return;
        if (source === "client" && !configurationId) {
            setItems([]);
            setPath("");
            setConnectionError("");
            return;
        }
        load("");
    }, [active, configurationId, load, source]);
    const toggle = (selectedPath: string, event?: MouseEvent<HTMLElement>) => {
        const filePaths = items.filter((item) => item.type !== "directory").map((item) => item.path);
        if (event?.ctrlKey && event.shiftKey && event.button === 0 && selectionAnchor) {
            const first = filePaths.indexOf(selectionAnchor);
            const last = filePaths.indexOf(selectedPath);
            if (first !== -1 && last !== -1) {
                const range = filePaths.slice(Math.min(first, last), Math.max(first, last) + 1);
                onChange(Array.from(new Set([...paths, ...range])));
                return;
            }
        }
        setSelectionAnchor(selectedPath);
        onChange(paths.includes(selectedPath) ? paths.filter((item) => item !== selectedPath) : [...paths, selectedPath]);
    };
    return <div style={{flex: 1}}>
        <Typography variant="subtitle1" style={{display: "flex", alignItems: "center", gap: 6}}><FiFolder/>{source === "client" ? "FTP cliente" : "FTP generale"}{paths.length ? ` (${paths.length})` : ""}</Typography>
        <div style={{marginTop: 8, border: "1px solid #ddd", borderRadius: 4, minHeight: 80, maxHeight: 180, overflow: "auto", padding: 8}}>
            {loading && <LinearProgress/>}
            {connectionError && <Typography variant="body2" color="error">{connectionError}</Typography>}
            {!loading && !connectionError && source === "client" && !configurationId &&
                <Typography variant="body2" color="textSecondary">Seleziona una configurazione per collegare l'FTP del cliente</Typography>}
            {!loading && !connectionError && <>
            {path && <Button size="small" onClick={() => load("")}>Cartella principale</Button>}
            {items.map((item) => item.type === "directory" ? <Button key={item.path} size="small" startIcon={<FiFolder/>} onClick={() => load(item.path)}>{item.name}</Button> : <FormControlLabel key={item.path} style={{display: "flex", margin: 0}} control={<Checkbox checked={paths.includes(item.path)} onClick={(event) => toggle(item.path, event)} onChange={() => undefined}/>} label={<span style={{display: "flex", alignItems: "center", gap: 6}}><FiFile/><span><span style={{display: "block"}}>{item.name}</span><Typography component="span" variant="caption" color="textSecondary">{`${(item.extension || "file").toUpperCase()} · ${item.size} MB${item.modified_at_display ? ` · ${item.modified_at_display}` : ""}`}</Typography></span></span>}/>) }
            {!!items.length || (source === "client" && !configurationId) ? null : <Typography variant="body2" color="textSecondary">Nessun file disponibile</Typography>}
            {!!items.length && <Typography variant="caption" color="textSecondary">Ctrl + Shift + clic sinistro seleziona un intervallo di file.</Typography>}
            </>}
        </div>
    </div>;
};

const CreateImportButton = () => {
    const maxUploadSize = 500 * 1024 * 1024;
    const [open, setOpen] = useState(false);
    const [configuration, setConfiguration] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [staticFtpPaths, setStaticFtpPaths] = useState<string[]>([]);
    const [clientFtpPaths, setClientFtpPaths] = useState<string[]>([]);
    const [ftpDaysBack, setFtpDaysBack] = useState(3);
    const [dragActive, setDragActive] = useState(false);
    const [autoStart, setAutoStart] = useState(true);
    const [saving, setSaving] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();
    const inputRef = useRef<HTMLInputElement>(null);
    const selectFiles = (selectedFiles: FileList | File[]) => setFiles(Array.from(selectedFiles));
    const dropFiles = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragActive(false);
        if (event.dataTransfer.files.length) selectFiles(event.dataTransfer.files);
    };
    const create = async () => {
        if (!configuration) {
            notify("Seleziona una configurazione", "warning");
            return;
        }
        if (files.some((file) => file.size > maxUploadSize)) {
            notify("Ogni file può avere una dimensione massima di 500 MB", "warning");
            return;
        }
        setSaving(true);
        try {
            const {data: created} = await clientNoJson(`${endpoint}/note-imports/`, {
                method: "POST",
                data: {configuration: Number(configuration)}
            });
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);
                await clientNoJson(`${endpoint}/note-imports/${created.id}/upload/`, {
                    method: "POST",
                    data: formData,
                    headers: {"Content-Type": "multipart/form-data"}
                });
            }
            if (staticFtpPaths.length || clientFtpPaths.length) await clientNoJson(`${endpoint}/note-imports/${created.id}/ftp-files/`, {method: "POST", data: {static_paths: staticFtpPaths, client_paths: clientFtpPaths}});
            if (autoStart && (files.length || staticFtpPaths.length || clientFtpPaths.length)) await clientNoJson(`${endpoint}/note-imports/${created.id}/execute/`, {method: "POST"});
            notify("Import creato", "info");
            setOpen(false);
            setFiles([]);
            setStaticFtpPaths([]);
            setClientFtpPaths([]);
            setConfiguration("");
            refresh();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile creare l’import"), "warning");
        } finally {
            setSaving(false);
        }
    };
    return <>
        <RaButton
            label="Aggiungi import"
            onClick={() => setOpen(true)}
        />
        <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="lg">
            <DialogTitle>Importa file</DialogTitle>
            <DialogContent>
                <Typography variant="body2" gutterBottom>Configurazione</Typography>
                <ConfigurationSelector value={configuration} onChange={setConfiguration} active={open}/>
                <input ref={inputRef} type="file" multiple accept=".tif,.tiff,.jpg,.jpeg,.png,.bmp,.gif,.pdf,.zip" hidden
                       onChange={(event: ChangeEvent<HTMLInputElement>) => selectFiles(event.target.files || [])}/>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={() => inputRef.current?.click()}
                    onKeyDown={(event) => event.key === "Enter" && inputRef.current?.click()}
                    onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={dropFiles}
                    style={{marginTop: 24, padding: "32px 16px", border: `2px dashed ${dragActive ? "#1976d2" : "#9e9e9e"}`, borderRadius: 8, textAlign: "center", cursor: "pointer", backgroundColor: dragActive ? "#e3f2fd" : "#fafafa"}}
                >
                    <Typography variant="subtitle1">Trascina qui i file oppure clicca per selezionarli</Typography>
                    <Typography variant="body2" color="textSecondary">TIFF, immagini, PDF e ZIP — massimo 500 MB per file</Typography>
                </div>
                <Typography variant="body2"
                            color="textSecondary">{files.length ? `${files.length} file selezionati` : "Nessun file selezionato"}</Typography>
                <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 16}}>
                    <Typography variant="body2" color="textSecondary">Mostra file degli ultimi</Typography>
                    <MuiTextField type="number" value={ftpDaysBack} inputProps={{min: 1, step: 1}} style={{width: 70}}
                                  onChange={(event) => setFtpDaysBack(Math.max(Number(event.target.value) || 1, 1))}/>
                    <Typography variant="body2" color="textSecondary">giorni</Typography>
                </div>
                <div style={{display: "flex", gap: 16, marginTop: 20}}>
                    <FtpPicker source="client" configurationId={configuration} paths={clientFtpPaths} onChange={setClientFtpPaths} active={open} daysBack={ftpDaysBack}/>
                    {/*<FtpPicker source="static" configurationId={configuration} paths={staticFtpPaths} onChange={setStaticFtpPaths} active={open} daysBack={ftpDaysBack}/>*/}
                </div>
                <FormControlLabel
                    control={<Checkbox checked={autoStart} onChange={(event) => setAutoStart(event.target.checked)}/>}
                    label="Avvia import automaticamente"/>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)} disabled={saving}>Annulla</Button>
                <Button color="primary" variant="contained" onClick={create} disabled={saving}>Crea</Button>
            </DialogActions>
        </Dialog>
    </>;
};

export const NoteImportList = (props: ListProps) => {
    const {data: options} = useOptionsResource("GET");
    const filters = options ? [<TextInput {...initField("id", options)}/>,
        <TextInput source="config_all" label="Configurazione o cliente"/>,
        <ReferenceInput {...initField("client", options)} reference="autocomplete/subject" filter={{type: "client"}} lable={"Cliente"} >
            <AutocompleteInput optionText="label"/>
        </ReferenceInput>,
        <SelectInput {...initField("status", options, "array")} choices={options.status.choices}/>,
        <SelectInput source="note_status" label="Stato bolle" choices={noteStatusChoices}
                     optionText="display_name" optionValue="value" alwaysOn/>,
        <DateInput source={"finished_date_after"} label={"Data dal"} alwaysOn/>,
        <DateInput source={"finished_date_before"} label={"Data a"}/>,
        <NullableBooleanInput source="hide" label="Nascosto"/>] : [];
    return options ? <CustomList {...getDefaultListProps(props)} filters={filters} sort={{field: "id", order: "DESC"}}
                                 filterDefaultValues={{hide: false}} additionalActions={<CreateImportButton/>} topElement={<NoteImportListHeader/>}
                                 bulkActionButtons={<><MoveImportFilesToNotesBulkButton/><HideNoteImportsBulkButton/><ShowNoteImportsBulkButton/><BulkDeleteButton/></>}
                                 empty={false}>
        <NoteImportDatagrid options={options}/>
    </CustomList> : null;
};

const MoveImportFilesToNotesBulkButton = () => {
    const {selectedIds, refetch} = useListContext();
    const unselectAll = useUnselectAll("note-imports");
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const moveSelected = async () => {
        setLoading(true);
        try {
            const {data} = await clientNoJson(`${endpoint}/note-imports/files/move-to-notes/`, {
                method: "POST", data: {ids: selectedIds}
            });
            notify(data.moved ? `${data.moved} bolle spostate in assegna bolle` : "Nessuna bolla non riconosciuta da spostare", "info");
            unselectAll();
            refetch();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile spostare le bolle selezionate"), "warning");
        } finally {
            setLoading(false);
        }
    };

    return <RaButton label="Sposta in assegna bolle" disabled={loading || selectedIds.length === 0} onClick={moveSelected}>
        <FiCornerUpRight/>
    </RaButton>;
};

const HideNoteImportsBulkButton = () => {
    const {selectedIds, refetch} = useListContext();
    const unselectAll = useUnselectAll("note-imports");
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const hideSelected = async () => {
        setLoading(true);
        try {
            const {data} = await clientNoJson(`${endpoint}/note-imports/hide/`, {
                method: "POST", data: {ids: selectedIds}
            });
            notify(data.hidden ? `${data.hidden} import nascosti` : "Nessun import completato da nascondere", "info");
            unselectAll();
            refetch();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile nascondere gli import selezionati"), "warning");
        } finally {
            setLoading(false);
        }
    };

    return <RaButton label="Nascondi" disabled={loading || selectedIds.length === 0} onClick={hideSelected}>
        <VisibilityOffIcon/>
    </RaButton>;
};

const ShowNoteImportsBulkButton = () => {
    const {selectedIds, refetch} = useListContext();
    const unselectAll = useUnselectAll("note-imports");
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const showSelected = async () => {
        setLoading(true);
        try {
            const {data} = await clientNoJson(`${endpoint}/note-imports/show/`, {
                method: "POST", data: {ids: selectedIds}
            });
            notify(data.shown ? `${data.shown} import resi visibili` : "Nessun import nascosto da mostrare", "info");
            unselectAll();
            refetch();
        } catch (error: any) {
            notify(errorMessage(error, "Impossibile mostrare gli import selezionati"), "warning");
        } finally {
            setLoading(false);
        }
    };

    return <RaButton label="Mostra" disabled={loading || selectedIds.length === 0} onClick={showSelected}>
        <VisibilityIcon/>
    </RaButton>;
};

const NoteImportDatagrid = ({options, ...rest}: { options: any; [key: string]: any }) => {
    const {data, ids, refetch} = useListContext();
    const activeImportExists = ids.some((id) => {
        const status = (data as any)[id]?.status;
        return status === "in_queue" || status === "in_progress";
    });
    useEffect(() => {
        if (!activeImportExists) return;
        const timer = window.setInterval(refetch, 3000);
        return () => window.clearInterval(timer);
    }, [activeImportExists, refetch]);
    return <Datagrid {...rest} expand={<ImportFiles/>}>
        <IdentifierField {...initField("id", options)}/><TextField source="configuration_name"
                                                                   label="Configurazione"/>
        <FunctionField label="Stato" render={(record: any) => <ImportStatusBadge status={record.status}
                                                                                 label={record.status_display}/>}/>
        <FunctionField label="Avanzamento"
                       render={(record: any) => <div style={{minWidth: 100}}><LinearProgress variant="determinate"
                                                                                             value={record.progress || 0}/><small>{record.progress || 0}%</small>
                       </div>}/>
        <FunctionField label="File" render={(record: any) => <ImportFileBadges files={record.files || []}/>}/><DateField
        source="finished_date" label="Completato"/>
        <FunctionField label="Azioni" render={() => <ImportActions/>}/>
    </Datagrid>;
};

const ImportStatusBadge = ({status, label}: { status: string; label: string }) => {
    const colors: Record<string, "default" | "primary" | "secondary"> = {
        start: "default", in_queue: "secondary", in_progress: "primary", completed: "primary", error: "secondary",
    };
    const style: Record<string, CSSProperties> = {
        completed: {backgroundColor: "#2e7d32", color: "white"},
        error: {backgroundColor: "#d32f2f", color: "white"},
        in_queue: {backgroundColor: "#ed6c02", color: "white"},
        in_progress: {backgroundColor: "#1976d2", color: "white"},
    };
    return <Chip size="small" label={label} color={colors[status] || "default"} style={style[status]}/>;
};

const ImportFileBadges = ({files}: { files: any[] }) => {
    const associated = files.filter((file) => Boolean(file.delivery_identifier)).length;
    const skipped = files.filter((file) => Boolean(file.skipped)).length;
    const notAssociated = files.length - associated - skipped;
    return <div style={{display: "flex", gap: 2}}>
        <Chip size="small" label={`${associated}`} style={{backgroundColor: "#2e7d32", color: "white"}}/>
        <Chip size="small" label={`${notAssociated}`} style={{backgroundColor: "#d32f2f", color: "white"}}/>
        {!!skipped && <Chip size="small" label={`${skipped} saltate`} style={{backgroundColor: "#ed6c02", color: "white"}}/>}
    </div>;
};
